import { DateTime } from 'luxon'

// Same thresholds as the backend's StudentInsights::Reason#cadence, so this
// matches the pace in the reason line shown above it.
export const formatCadence = days => {
  if (!days) return 'Not enough lessons yet'
  if (days === 7) return 'Every week'
  if (days < 14) return `Every ${days} ${days === 1 ? 'day' : 'days'}`
  const weeks = Math.round(days / 7)
  return weeks < 8
    ? `Every ${weeks} weeks`
    : `Every ${Math.round(days / 30)} months`
}

export const formatCents = cents =>
  cents == null
    ? '—'
    : new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(cents / 100)

export const formatLessonDate = iso => {
  if (!iso) return 'None'
  const date = DateTime.fromISO(iso)
  const format = date.hasSame(DateTime.local(), 'year')
    ? 'LLL d'
    : 'LLL d, yyyy'
  return date.toFormat(format)
}

const plural = (count, unit) => `${count} ${unit}${count === 1 ? '' : 's'}`

// Relative time since a student's most recent completed lesson. Shown under a
// "Last lesson" label, so the text itself leaves "lesson" off.
export const formatLastLesson = iso => {
  if (!iso) return 'No lesson yet'
  const days = Math.floor(
    DateTime.local()
      .startOf('day')
      .diff(DateTime.fromISO(iso).startOf('day'), 'days').days,
  )
  if (days < 1) return 'Today'
  if (days < 14) return `${plural(days, 'day')} ago`
  const weeks = Math.round(days / 7)
  if (weeks < 8) return `${plural(weeks, 'week')} ago`
  const months = Math.round(days / 30)
  if (months < 12) return `${plural(months, 'month')} ago`
  return `${plural(Math.round(days / 365), 'year')} ago`
}

// Visible line under the status tag. The backend's reason copy
// (StudentInsights::Reason) is long for the narrow status column, so the known
// shapes are shortened to one "Last lesson ..." / "First lesson ..." /
// "Canceled ..." pattern. "Next lesson ..." is left off the line (null); it
// and the full reason stay in the tag tooltip. Unknown copy is shown as is.
const AGO = '(today|yesterday|\\d+ (?:days?|weeks?|months?) ago)'

const shortAgo = ago =>
  ago
    .replace(/(\d+) weeks?/, (_, n) => `${n} ${n === '1' ? 'wk' : 'wks'}`)
    .replace(/(\d+) months?/, '$1 mo')

const INSIGHT_REASON_RULES = [
  [/^next lesson\b/i, () => null],
  [
    new RegExp(`^(?:usually books every .+?, )?last lesson ${AGO}$`, 'i'),
    ago => `Last lesson ${shortAgo(ago)}`,
  ],
  [
    /^no lessons in (\d+ (?:days?|weeks?|months?))$/i,
    span => `Last lesson ${shortAgo(`${span} ago`)}`,
  ],
  [/^last lesson over a year ago$/i, () => 'Last lesson 1+ yr ago'],
  [
    new RegExp(`^first lesson ${AGO}, nothing booked yet$`, 'i'),
    ago => `First lesson ${shortAgo(ago)}`,
  ],
  [
    new RegExp(`^canceled a booking ${AGO} and hasn't rebooked$`, 'i'),
    ago => `Canceled ${shortAgo(ago)}`,
  ],
  [/^no completed lessons yet$/i, () => 'No lessons yet'],
]

export const getInsightReasonLine = reason => {
  const text = reason?.trim()
  if (!text) return null

  for (const [pattern, format] of INSIGHT_REASON_RULES) {
    const match = text.match(pattern)
    if (match) return format(match[1])
  }
  return text
}
